import React, { useState } from 'react';
import { OverFlowIcon, UIAddIcon, UIDeleteIcon, UIDuplicateIcon } from 'assests/GmsIcons';
import { DropdownMenu, DropdownMenuItem } from '../../Common/DropdownMenu';
import { ConfirmationDialog } from '../../Common/ConfirmationDialog';
import { ContainerItem, isWidgetItem } from '../../../types/canvas.types';

type ContainerHeaderProps = {
    id?: string;
    name: string;
    componentId?: string;
    containerType?: string;
    widgetType?: string;
    containerId: string;
    hasNestedContainers?: boolean;
    containerItems?: ContainerItem[];
    nestingLevel?: number;
    onRemoveComponent?: (componentId: string) => void;
    onAddContainer?: () => void;
    onDuplicateContainer?: () => void;
    onDeleteContainer?: () => void;
    onRemoveYourBuildWidget?: () => void;
    collapsible?: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export const ContainerHeader = ({
    id,
    name,
    componentId,
    containerType,
    widgetType,
    containerId,
    hasNestedContainers = false,
    containerItems = [],
    nestingLevel = 0,
    onRemoveComponent,
    onAddContainer,
    onDuplicateContainer,
    onDeleteContainer,
    onRemoveYourBuildWidget,
    collapsible = false,
    isCollapsed = false,
    onToggleCollapse
}: ContainerHeaderProps) => {
    const baseId = id || `container-header-${containerId}`;
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const MAX_NESTING_LEVEL = 2;

    const handleRemoveComponent = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onRemoveComponent && componentId) {
            onRemoveComponent(componentId);
        }
    };

    const dropdownItems: DropdownMenuItem[] = [];
    
    const hasYourBuildWidget = containerItems?.some(item =>
        isWidgetItem(item) && item.widgetType === 'yourBuild'
    );
    
    if (nestingLevel < MAX_NESTING_LEVEL) {
        dropdownItems.push({
            id: 'add-container',
            label: 'Add Container',
            icon: <UIAddIcon />,
            onClick: () => {
                if (onAddContainer) {
                    onAddContainer();
                }
            },
            className: 'tw-text-[#172B4D]',
            divider: true
        });
    }
    
    if (hasYourBuildWidget && onRemoveYourBuildWidget) {
        dropdownItems.push({
            id: 'remove-your-build',
            label: 'Remove Your Build Widget',
            icon: <UIDeleteIcon />,
            onClick: () => {
                onRemoveYourBuildWidget();
            },
            className: 'tw-text-orange-600',
            divider: true
        });
    }
    
    dropdownItems.push({
        id: 'duplicate',
        label: 'Duplicate',
        icon: <UIDuplicateIcon />,
        onClick: () => {
            if (onDuplicateContainer) {
                onDuplicateContainer();
            }
        },
        className: 'tw-text-[#172B4D]',
        divider: true
    });
    
    dropdownItems.push({
        id: 'delete',
        label: 'Delete',
        icon: <UIDeleteIcon />,
        onClick: () => {
            setShowDeleteConfirmation(true);
        },
        className: 'tw-text-[#172B4D]',
        divider: true
    });

    const handleConfirmDelete = () => {
        
        if (onDeleteContainer) {
            onDeleteContainer();
        }
        setShowDeleteConfirmation(false);
    };

    return (
        <>
            <div id={baseId} data-testid={baseId} className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-1 tw-bg-[#F0F0F3]">
                <div id={`${baseId}-title-section`} className="tw-flex tw-items-center tw-gap-2">
                    {collapsible && (
                        <button
                            id={`${baseId}-collapse-btn`}
                            data-testid={`${baseId}-collapse-btn`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleCollapse) {
                                    onToggleCollapse();
                                }
                            }}
                            className="tw-p-0.5 tw-text-[#6B788C] hover:tw-bg-gray-200 tw-rounded tw-transition-colors"
                            title={isCollapsed ? "Expand" : "Collapse"}
                        >
                            <svg
                                id={`${baseId}-collapse-icon`}
                                className={`tw-w-4 tw-h-4 tw-transition-transform ${isCollapsed ? '' : 'tw-rotate-90'}`}
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M6 4l4 4-4 4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    )}
                    <h3 id={`${baseId}-title`} className="tw-text-sm tw-font-medium tw-text-[#6B788C]">{name}</h3>
                </div>
                <div id={`${baseId}-actions`} className="tw-flex tw-items-center tw-space-x-2">
                    <DropdownMenu
                        id={`${baseId}-dropdown`}
                        trigger={
                            <button id={`${baseId}-menu-btn`} className="tw-p-1 tw-text-[#6B788C] hover:tw-bg-gray-200 tw-rounded">
                                <OverFlowIcon />
                            </button>
                        }
                        items={dropdownItems}
                        position="right"
                    />
                </div>
            </div>

            <ConfirmationDialog
                id={`${baseId}-delete-confirmation`}
                isOpen={showDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Container"
                message={
                    hasNestedContainers
                        ? `Are you sure you want to delete "${name}"? This will also delete all nested containers and their contents.`
                        : `Are you sure you want to delete "${name}"?`
                }
                confirmText="Delete"
                cancelText="Cancel"
            />
        </>
    );
};